import React from 'react';
import { Shield, Mail, Lock, Bell, Wallet, Users } from 'lucide-react';
import SEO from '../Components/shared/SEO';

const PrivacyPolicy = () => {
  return (
    <div className="max-w-4xl mx-auto md:p-6 min-h-screen">
      <SEO title="Privacy Policy" path="/privacy-policy" description="How Serenoly collects, uses, and protects the personal information of clients, therapists, and venue partners." />
      <div className="bg-white rounded-lg shadow-sm p-8 mt-20 md:my-20">
        <div className="flex items-center justify-center mb-8">
          <Shield className="h-8 w-8 text-primary mr-3" />
          <h1 className="text-3xl font-bold">Privacy Policy</h1>
        </div>

        <div className="space-y-8">
          <section>
            <p className="text-gray-600">
              Serenoly ("we", "our", "us") operates a marketplace platform that connects clients with
              verified massage therapists and partner venues (hotels, guesthouses, spas). This policy
              explains what information we collect from clients, therapists, and venue partners, and
              how we use it.
            </p>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-4">
              <Mail className="h-5 w-5 text-primary" />
              <h2 className="text-2xl font-semibold">Information We Collect</h2>
            </div>
            <div className="text-gray-600 space-y-4">
              <p>We collect the following types of information, depending on your role on the platform:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Personal identification information (name, email address, phone number)</li>
                <li>Location information, including service address or zone, used for real-time therapist matching</li>
                <li>Health and safety intake information relevant to a booked massage (e.g. medical conditions, medications)</li>
                <li>Booking, session, and rating history</li>
                <li>Payment and transaction information processed via Paystack (we do not store full card details)</li>
                <li>Wallet balances, bank account details, and withdrawal history (for therapists and venue partners)</li>
                <li>Identity and certification/verification documents submitted by therapists and venue partners</li>
                <li>Device, browser, and usage data collected automatically via analytics tools</li>
              </ul>
            </div>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-4">
              <Bell className="h-5 w-5 text-primary" />
              <h2 className="text-2xl font-semibold">How We Use Your Information</h2>
            </div>
            <div className="text-gray-600 space-y-4">
              <p>We use your information to:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Match clients with the best-available verified therapist and process bookings</li>
                <li>Process escrow payments, release earnings, and manage platform wallets</li>
                <li>Verify the identity and credentials of therapists and venue partners before they can accept bookings</li>
                <li>Facilitate dispute resolution and enforce our quality and safety policies (including our therapist strike policy)</li>
                <li>Send booking confirmations, order updates, and important account notifications</li>
                <li>Analyze site usage and improve the platform, using tools such as Google Analytics (GA4) and Hotjar</li>
                <li>Comply with legal, tax, and regulatory obligations</li>
              </ul>
            </div>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-5 w-5 text-primary" />
              <h2 className="text-2xl font-semibold">Sharing With Therapists & Venue Partners</h2>
            </div>
            <div className="text-gray-600 space-y-4">
              <p>
                To fulfill a booking, we share the information necessary for the session with the
                matched therapist (and, where applicable, the partner venue) &mdash; such as your name,
                contact details, location, and relevant health/safety intake information. We do not sell
                your personal information to third parties.
              </p>
            </div>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-4">
              <Wallet className="h-5 w-5 text-primary" />
              <h2 className="text-2xl font-semibold">Payments & Wallets</h2>
            </div>
            <div className="text-gray-600 space-y-4">
              <p>
                Payments are processed securely through Paystack and held in escrow until a session is
                confirmed complete by both parties. Bank account details submitted by therapists and
                venue partners for withdrawals are verified through Paystack's account resolution service
                and used solely to process payouts.
              </p>
            </div>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-4">
              <Lock className="h-5 w-5 text-primary" />
              <h2 className="text-2xl font-semibold">Data Protection & Security</h2>
            </div>
            <div className="text-gray-600 space-y-4">
              <p>We implement measures to protect your personal information, including:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Secure storage of personal, health, and verification data</li>
                <li>Limited access to personal data by authorized staff only</li>
                <li>Encrypted payment processing via Paystack</li>
                <li>Secure, access-controlled document storage for verification uploads</li>
                <li>Compliance with the Nigeria Data Protection Act (NDPA) and applicable regulations</li>
              </ul>
              <p>We retain your information only for as long as necessary to provide our services and comply with legal obligations.</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Cookies & Analytics</h2>
            <div className="text-gray-600 space-y-4">
              <p>
                We use Google Analytics (GA4) and Hotjar to understand how visitors use our platform and
                to improve the experience. These tools may use cookies and similar technologies to collect
                anonymized or pseudonymized usage data. You can control cookies through your browser
                settings.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Your Rights</h2>
            <div className="text-gray-600 space-y-4">
              <p>You have the right to:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Access the personal information we hold about you</li>
                <li>Request corrections to your data</li>
                <li>Request deletion of your information, subject to legal and financial record-keeping requirements</li>
                <li>Opt out of marketing communications</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
            <p className="text-gray-600">
              If you have any questions about this privacy policy or how we handle your data, please
              contact us at <a href="mailto:privacy@serenoly.udoemmanuel.com" className="text-primary hover:underline">privacy@serenoly.udoemmanuel.com</a>.
            </p>
          </section>

          <div className="text-sm text-gray-500 mt-8">
            Last updated: September 6, 2026
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;